import React from "react";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { styled } from "@mui/material/styles";
import { TooltipComponentProps } from "../../interface";

interface StyledTooltipProps extends TooltipProps {
  tooltipStyles?: React.CSSProperties;
}

const StyledTooltip = styled(
  // `className` is injected by styled() but typed optional, so it is spread in only when present:
  // MUI's `TooltipClasses` keys are `string`, and an explicit `undefined` popper class is not the
  // same as an absent one under exactOptionalPropertyTypes.
  ({ className, ...props }: StyledTooltipProps) => (
    <Tooltip {...props} classes={{ ...(className !== undefined && { popper: className }) }} />
  ),
  {
    shouldForwardProp: (prop) => prop !== "tooltipStyles",
  },
)(({ tooltipStyles }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "var(--primary-text)",
    color: "var(--surface-white)",
    fontSize: "0.875rem",
    fontWeight: 500,
    fontFamily: "inherit",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.25rem",
    gap: "0.5rem",
    ...tooltipStyles,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "var(--primary-text)",
  },
}));

const TOOLTIP_Z_INDEX = 13000;
// Stable references: a fresh `slots`/`slotProps` object each render makes MUI re-run useSlot and
// remount the Zoom transition, which can schedule an update → re-render → new object → render loop.
const TOOLTIP_SLOTS = { transition: Zoom };

const ToolTipComponent: React.FC<TooltipComponentProps> = (props) => {
  const { content, children, position, slotProps, ...rest } = props;
  const hasContent = !!content;

  const mergedSlotProps = React.useMemo(
    () => ({
      ...slotProps,
      popper: {
        ...slotProps?.popper,
        style: { zIndex: TOOLTIP_Z_INDEX, ...slotProps?.popper?.style },
      },
    }),
    [slotProps],
  );

  return (
    <StyledTooltip
      {...rest}
      slotProps={mergedSlotProps}
      placement={position || "top"}
      slots={TOOLTIP_SLOTS}
      arrow
      title={hasContent ? content : ""}
    >
      {/* MUI clones this child to attach the ref and the hover/focus handlers, so it must be a
          single element. `TooltipComponentProps.children` is a `ReactNode`, so anything that is not
          already an element (text, a number, `false`, nullish) is wrapped instead of asserted. */}
      {React.isValidElement(children) ? children : <span>{children}</span>}
    </StyledTooltip>
  );
};

ToolTipComponent.displayName = "ToolTipComponent";
export default ToolTipComponent;
